// AddToCart.js
// Landing para links directos de compra: /add/:planId
// La app de workouts manda al usuario aquí cuando le da "comprar" a un plan,
// lo agregamos al carrito y lo mandamos al carrito. #added 21/8/26 TG
import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { AppContext } from "./ContextVars";
import translations from "./translations";
import { phpPath } from "./config";
import { productize, isPurchasable, parsePlanId } from "./planHelpers";

const AddToCart = () => {
  const { planId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { cart, addToCart, language } = useContext(AppContext);
  const t = (key) => translations.AddToCart[key]?.[language] || key;

  const [status, setStatus] = useState("loading"); // loading | error | added | already
  const [errorKey, setErrorKey] = useState("");
  const [planName, setPlanName] = useState("");

  // #StrictMode corre los effects dos veces en dev, y sin este candado el plan
  // se agregaba dos veces al carrito. TG 21/8/26 TG
  const alreadyRan = useRef(false);

  // El id puede venir en la ruta (/add/31) o como query (?add=31 / ?plan=31)
  const requestedId = planId || searchParams.get("add") || searchParams.get("plan");

  useEffect(() => {
    if (alreadyRan.current) return;
    alreadyRan.current = true;

    const run = async () => {
      const id = parsePlanId(requestedId);
      if (!id) {
        setErrorKey("badLink");
        setStatus("error");
        return;
      }

      try {
        const response = await fetch(`${phpPath}/get-plans.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });
        const data = await response.json();

        if (!data.success || !Array.isArray(data.plans)) {
          setErrorKey("loadError");
          setStatus("error");
          return;
        }

        const plan = data.plans.find((p) => String(p.id) === String(id));
        if (!plan) {
          setErrorKey("notFound");
          setStatus("error");
          return;
        }

        setPlanName(plan.name || "");

        // Sin product_id o sin precio no se puede cobrar ni activar. Mejor
        // avisar que meter algo roto al carrito. TG
        if (!isPurchasable(plan)) {
          setErrorKey("notPurchasable");
          setStatus("error");
          return;
        }

        const item = productize(plan);

        // Si ya está en el carrito no lo duplicamos (el carrito no maneja
        // cantidades todavía, así que un duplicado se cobraría dos veces). TG
        const dupe = cart.some((it) => String(it.plan_id) === String(plan.id));
        if (dupe) {
          setStatus("already");
          return;
        }

        addToCart(item);
        setStatus("added");
      } catch (error) {
        console.error("#AddToCart error:", error);
        setErrorKey("networkError");
        setStatus("error");
      }
    };

    run();
  }, [requestedId, cart, addToCart]);

  // Ya quedó: nos vamos al carrito. TG
  useEffect(() => {
    if (status === "added" || status === "already") {
      const timer = setTimeout(() => navigate("/cart"), 900);
      return () => clearTimeout(timer);
    }
  }, [status, navigate]);

  // #added para que el contenido quede por encima del overlay (tint-black) TG
  const contentStyle = { position: "relative", zIndex: 1 };

  return (
    <section className="section-2 overlay tint-black-40">
      <div className="p-4 max-w-md mx-auto" style={contentStyle}>
        <h2 className="general-h2 margin-top-70">{t("title")}</h2>

        {status === "loading" && (
          <p className="margin-top-30 font-size-20 color-gray-3 margin-bottom-100">{t("loading")}</p>
        )}

        {(status === "added" || status === "already") && (
          <div>
            <p className="margin-top-30 font-size-20 color-gray-3">
              {status === "added" ? t("added") : t("alreadyInCart")}
              {planName ? `: ${planName}` : ""}
            </p>
            <div className="add-cart-actions">
              <Link to="/cart" className="button-2">{t("goToCart")}</Link>
            </div>
          </div>
        )}

        {status === "error" && (
          <div>
            <p className="margin-top-30 font-size-20 color-gray-3">{t(errorKey)}</p>
            <div className="add-cart-actions">
              <Link to="/program" className="button-2">{t("seePrograms")}</Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default AddToCart;
